/**
 * Utility functions for infinite scroll functionality
 * Can be used by both class and functional components
 */

/**
 * Creates infinite scroll state and handlers for class components
 * @param {Object} component - The component instance (this)
 * @param {Object} options - Configuration options
 * @param {Function} options.loadMore - Function to call when loading more data
 * @param {number} options.currentLength - Current number of items loaded
 * @param {number} options.totalItems - Total number of items available
 * @param {number} options.cooldownMs - Cooldown period in milliseconds (default: 300)
 * @returns {Object} Infinite scroll state and handlers
 */
export const createInfiniteScrollHandlers = (component, {
  loadMore,
  currentLength,
  totalItems,
  cooldownMs = 300,
}) => {
  const hasMore = () => {
    // Don't show "has more" if we haven't loaded any items yet
    if (!currentLength || currentLength === 0) {
      return false;
    }
    
    // If we're currently loading, still return true so the loader shows
    if (component.state.loadingMore) {
      return true;
    }
    
    return currentLength < totalItems;
  };

  const handleLoadMore = () => {
    // Prevent multiple simultaneous loads
    if (component.state.loadingMore) {
      return;
    }
    
    // Prevent loading too frequently (cooldown period)
    const now = Date.now();
    const timeSinceLastLoad = now - (component.state.lastLoadTime || 0);
    
    if (timeSinceLastLoad < cooldownMs) {
      return;
    }
    
    component.setState({ loadingMore: true, lastLoadTime: now });
    
    // Call the loadMore function
    if (loadMore) {
      loadMore();
    }
  };

  const setLoadingComplete = () => {
    component.setState({ loadingMore: false });
  };

  const shouldShowEndMessage = () => {
    // Only show end message if we have items loaded AND there are no more to load AND we're not currently loading
    return currentLength && 
           currentLength > 0 && 
           !hasMore() && 
           !component.state.loadingMore;
  };

  return {
    hasMore: hasMore(),
    hasMoreFn: hasMore,
    handleLoadMore,
    setLoadingComplete,
    shouldShowEndMessage: shouldShowEndMessage(),
    shouldShowEndMessageFn: shouldShowEndMessage,
  };
};

/**
 * Which page to request next, derived from what is actually loaded instead of from a counter.
 *
 * A collection restored in a single request (fetchCurrent asks for per = max(perPage, loaded) on
 * page 1) leaves the page counter at 1 while several pages of rows are on screen. Incrementing that
 * counter re-requests rows the list already has; the merge adds nothing, dataLength never changes,
 * and react-infinite-scroll-component only clears showLoader / actionTriggered when dataLength
 * changes - so the loader spins forever and next() is never called again.
 *
 * The page size is floored before it is used. Callers derive perPage from the viewport and some of
 * them hand over a fraction (e.g. (innerHeight - 110) / 63 + 1); Kaminari truncates `per` server
 * side, so such a page can never come back full, loaded / perPage never reaches the next whole
 * number, and this would hand back the page that was just fetched - forever.
 *
 * @param {number} loaded - items currently in the collection
 * @param {number} perPage - page size used for paged requests
 * @param {number} currentPage - page counter, used only as a fallback
 * @returns {number} 1-based page to request next
 */
export const nextPageForLoadedItems = (loaded, perPage, currentPage) => {
  const pageSize = Math.floor(perPage);
  if (!pageSize || !loaded) {
    return (currentPage || 0) + 1;
  }
  return Math.floor(loaded / pageSize) + 1;
};

/**
 * Default infinite scroll configuration
 */
export const DEFAULT_INFINITE_SCROLL_CONFIG = {
  scrollThreshold: 0.9,
  cooldownMs: 300,
};



// WEBPACK FOOTER //
// ./src/utils/infiniteScrollUtils.js